<template>
  <div>
    <div class="calculator-container" :class="{ 'remove-margin': removeMargin }">
      <h1 class="h3">
        Variance Calculator
      </h1>
      <div class="label-tooltip your-data-tooltip">
        <span class="your-data">
          Your Data
        </span>
        <div class="icon-info-tooltip variance">
          <IconInfo />
          <Tooltip>
            <span>Select <strong>Population</strong> if the data contains all measurable values or all values you are interested in.<br>
              Select <strong>Sample</strong> if the data is a sample of a large or unlimited population and you wish to make a statement about the entire population.</span>
          </Tooltip>
        </div>
      </div>
      <Dropdown
        class="calculation-method"
        :reset="resetProp"
        calculator
        selected-title="Sample"
        :selected="selectedCalculationMethod"
        :radio-buttons="{ sample: 'Sample', population: 'Population' }"
        @change="currentSelectedCalculationMethod"
      />
      <form @submit.prevent="calculations">
        <div class="raw-data">
          <div class="label-tooltip">
            <label class="type-data">
              Enter Data
            </label>
            <div class="icon-info-tooltip variance">
              <IconInfo />
              <Tooltip>
                <span>Input the data separated by comma, space or enter. To calculate variance, you need at least two values in your dataset.</span>
              </Tooltip>
            </div>
          </div>
          <div class="textarea-border" :class="{ 'invalid-value': textareaAlert }">
            <textarea
              v-model="rawDataMean"
              cols="30" rows="10"
              placeholder="7,1,6,2,11

7 1 6 2 11

7
1
6"
            />
          </div>
          <div v-if="textareaAlert" class="error">
            {{ textareaAlert }}
          </div>
        </div>
        <div class="decimal-places">
          <div class="label-tooltip">
            <label>
              Decimal Places
            </label>
            <div class="icon-info-tooltip variance">
              <IconInfo />
              <Tooltip>
                <span>Choose the number of decimal places.</span>
              </Tooltip>
            </div>
          </div>
          <Dropdown
            calculator
            calculator-decimal-places
            class="small-dropdown"
            :selected="selectedDecimalPlaces"
            :radio-buttons="{ 1: '1', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9' }"
            @change="currentSelectedDecimalPlace"
          />
        </div>
        <div class="buttons">
          <button class="btn-primary calculate">
            Calculate
          </button><button
            class="btn-primary-light reset"
            @click="reset"
          >
            Reset
          </button>
          <button class="load-example btn-link-accent" @click="loadExample">
            Load Example
          </button>
        </div>
      </form>
    </div>
    <div
      v-show="variance" id="summary" class="summary-container"
    >
      <h4>Summary:</h4>
      <div :key="symbols[0]" class="results">
        <div>
          <span class="result-name">Variance:</span>
          <span class="result-value">
            <span>\[{{ symbols[0] }}^2\]</span> = {{ variance }}</span>
        </div>
        <div>
          <span class="result-name">Standard Deviation:</span>
          <span class="result-value"><span>\[{{ symbols[0] }}\]</span> = {{ standardDeviation }}</span>
        </div>
        <div>
          <span class="result-name">Number of Observations:</span>
          <span class="result-value">n = {{ size }}</span>
        </div>
        <div>
          <span class="result-name">Mean:</span>
          <span class="result-value"><span>\[\{{ symbols[1] }}\]</span> = {{ mean }}</span>
        </div>
        <div>
          <span class="result-name">Sum of Squares:</span>
          <span class="result-value">SS = {{ sumOfSquaredDeviations }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Dropdown from '@itbpbg/lms-components/src/components/Dropdown.vue';
import Tooltip from '@itbpbg/lms-components/src/components/Tooltip.vue';
import IconInfo from '@itbpbg/lms-components/src/assets/svg/icon-info.svg?vue-component';

export default {
  components: {
    Dropdown, Tooltip, IconInfo,
  },
  data() {
    return {
      variance: '',
      mean: '',
      size: '',
      standardDeviation: '',
      sumOfSquaredDeviations: '',
      deviationsFromMean: '',
      deviationsFromMeanSquared: '',
      rawDataMeanArray: [],
      rawDataMean: '',
      selectedCalculationMethod: 'sample',
      textareaAlert: false,
      selectedDecimalPlaces: '4',
      removeMargin: false,
      resetProp: false,
      resultNumber: 0,
    };
  },
  computed: {
    hideResultVariablesArray() {
      return [
        this.rawDataMean,
        this.selectedDecimalPlaces,
      ];
    },
    symbols() {
      return this.selectedCalculationMethod === 'sample' ? ['s', 'bar{X}'] : ['\\sigma', 'mu'];
    },
  },
  watch: {
    selectedCalculationMethod() {
      this.defaultingInput();
    },
    hideResultVariablesArray() {
      this.hideResults();
    },
  },
  mounted() {
    this.$store.commit('calculators/result', false);
  },
  methods: {
    currentSelectedCalculationMethod(currentMethod) {
      this.selectedCalculationMethod = currentMethod;
    },
    async scrollToSection() {
      this.$nextTick(() => {
        let yOffset;
        if (this.$store.state.layout === 'desktop') {
          yOffset = -90;
        } else {
          yOffset = this.$config.project === 'lms' ? -64 : -68;
        }

        const section = 'summary';
        const element = document.querySelector(`#${section}`);
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        setTimeout(() => {
          this.clickedSection = section;
          window.scrollTo({ top: y, behavior: 'smooth' });

          setTimeout(() => {
            this.clickedSection = null;
          }, 500);
        }, 200);
      });
    },
    reset(e) {
      this.selectedCalculationMethod = 'sample';
      this.defaultingInput();
      e.preventDefault();
    },
    defaultingInput() {
      this.resetProp = true;
      this.rawDataMean = '';
      this.selectedDecimalPlaces = '4';
      this.variance = '';
      this.$store.commit('calculators/result', false);
      this.textareaAlert = '';
    },
    hideResults() {
      this.removeMargin = false;
      this.variance = '';
      this.$store.commit('calculators/result', false);
      this.textareaAlert = '';
    },
    loadExample(e) {
      this.selectedDecimalPlaces = '4';
      this.rawDataMean = '5\n3\n6\n7\n8\n9\n12';
      this.hideResults();

      e.preventDefault();
    },
    currentSelectedDecimalPlace(selectedDecimalPlaces) {
      this.selectedDecimalPlaces = selectedDecimalPlaces;
    },
    inputValidation() {
      this.textareaAlert = '';
      this.rawDataMeanArray = this.rawDataMean.split(/,|\s+|\n+/).filter((value) => value !== '');

      if (this.rawDataMeanArray.length === 0) {
        this.textareaAlert = 'Field required';
        this.rawDataMean = '';
        return false;
      }

      for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
        if (this.rawDataMeanArray[i] < 0 || Number.isNaN(Number(this.rawDataMeanArray[i]))) {
          this.textareaAlert = 'Invalid value';
          return false;
        }
      }

      if (this.rawDataMeanArray.length < 2 && this.rawDataMeanArray.length > 0) {
        this.textareaAlert = 'To calculate variance, you need at least two values in your dataset.';

        return false;
      }

      return true;
    },
    calculations() {
      if (!this.inputValidation()) {
        return;
      }

      this.mean = 0;
      this.size = this.rawDataMeanArray.length;
      this.standardDeviation = 0;
      this.sumOfSquaredDeviations = 0;
      this.deviationsFromMean = 0;
      this.deviationsFromMeanSquared = 0;
      const zeroOrOne = this.selectedCalculationMethod === 'sample' ? 1 : 0;

      // Calculating the mean
      for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
        this.mean += Number(this.rawDataMeanArray[i]);
      }

      this.mean /= this.size;

      // Calculate the deviations from the mean
      this.deviationsFromMean = this.rawDataMeanArray.map((num) => num - this.mean);

      // Each deviation squaring
      this.deviationsFromMeanSquared = this.deviationsFromMean.map((num) => num ** 2);

      // Calculating the sum of the squared deviations
      this.sumOfSquaredDeviations = this.deviationsFromMeanSquared.reduce((a, b) => a + b, 0);

      // Calculating sample/population standard deviation
      for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
        this.standardDeviation += (Number(this.rawDataMeanArray[i]) - this.mean) ** 2;
      }

      this.standardDeviation = Math.sqrt(this.standardDeviation / (this.size - zeroOrOne));

      // Calculating the variance
      this.variance = this.sumOfSquaredDeviations / (this.size - zeroOrOne);

      this.removeMargin = true;
      this.checkAndFormatResult();
      this.calculationData();
      this.$store.commit('calculators/result', this.resultNumber += 1);
      this.scrollToSection();
    },
    checkAndFormatResult() {
      const onlyZerosTest = /^\d+.[0]+$/;
      const resultsArray = [
        ['standardDeviation', this.standardDeviation.toString()],
        ['variance', this.variance.toString()],
        ['mean', this.mean.toString()],
        ['sumOfSquaredDeviations', this.sumOfSquaredDeviations.toString()],
        ['deviationsFromMean', this.deviationsFromMean[0].toString()],
        ['deviationsFromMeanSquared', this.deviationsFromMeanSquared[0].toString()],
      ];

      for (let i = 0; i < resultsArray.length; i += 1) {
        if (resultsArray[i][1].split('').includes('e')) {
          [resultsArray[i][1]] = resultsArray[i][1].split('e');
        }

        if (onlyZerosTest.test(resultsArray[i][1])) {
          resultsArray[i][1] = Math.trunc(resultsArray[i][1]).toString();
        }

        this[resultsArray[i][0]] = Number(resultsArray[i][1]).toFixed(this.selectedDecimalPlaces);
      }
    },
    calculationData() {
      this.$store.commit('calculators/calculationData',
        {
          rawDataMeanArray: this.rawDataMeanArray,
          method: this.selectedCalculationMethod,
          mean: this.mean,
          standardDeviation: this.standardDeviation,
          selectedDecimalPlaces: this.selectedDecimalPlaces,
          sumOfSquaredDeviations: this.sumOfSquaredDeviations,
          deviationsFromMeanFirstValue: this.deviationsFromMean,
          deviationsFromMeanSquaredFirstValue: this.deviationsFromMeanSquared,
          size: this.size,
          variance: this.variance,
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.calculator-container {
  .decimal-places {
    width: calc(50% - 15px);
    margin-top: var(--spacing-30);
    margin-bottom: var(--spacing-36);

    .dropdown::v-deep {
      &.open {
        .list {
          width: 100.63%;
          left: -1px;
        }
      }
    }
  }

  .label-tooltip .icon-info-tooltip {
    margin: 0;
  }

  .raw-data {
    .label-tooltip .icon-info-tooltip {
      &.variance {
        &:hover {
          .tooltip {
            height: auto;
          }
        }
      }
    }
  }
}

.summary-container {
  min-height: unset !important;

  .results {
    min-height: unset !important;

    div {
      justify-content: space-between;
      padding: 0 var(--spacing-80) 0 10px;

      &:nth-child(even) {
        background: rgba(218, 246, 251, 0.55);
      }

      span.result-name {
        width: unset;
      }

      .result-value {
        span {
          ::v-deep .MathJax_SVG_Display {
            display: unset !important;
            margin: 0;
          }
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .calculator-container {
    &.remove-margin {
      margin-bottom: 0;
    }

    .decimal-places {
      .label-tooltip .icon-info-tooltip {
        &.variance {
          &:hover {
            .tooltip:after {
              left: 49.7%;
            }
          }
        }
      }
    }

    .raw-data {
      .label-tooltip .icon-info-tooltip {
        &.variance {
          &:hover {
            .tooltip {
              left: 130%;

              &:after {
                left: 42.15%;
              }
            }
          }
        }
      }
    }
  }

  div .summary-container {
    margin-top: var(--spacing-60);
  }
}

@include bp($bp-phone) {
  .calculator-container {
    .decimal-places {
      width: 100%;

      .dropdown::v-deep {
        &.open {
          .list {
            top: 101.9%;
          }
        }
      }

      .label-tooltip .icon-info-tooltip {
        &.variance {
          &:hover {
            .tooltip:after {
              left: 46%;
            }
          }
        }
      }
    }

    .raw-data {
      .label-tooltip .icon-info-tooltip {
        &.variance {
          &:hover {
            .tooltip {
              left: 271%;

              &:after {
                left: 34.5%;
              }
            }
          }
        }
      }
    }
  }

  .summary-container {
    div {
      overflow-y: hidden;

      .result-value {
        white-space: nowrap;
      }
    }
  }

  div .interpretation-container {
    padding: var(--spacing-32) var(--spacing-20);
  }
}

@import '../../../../assets/sass/ds/calculators/calculators.scss';
</style>
